import React from "react";
import { Alert, Autocomplete, Box, Button, Chip, FormControlLabel, IconButton, LinearProgress, Link, Paper, Switch, Tab, Tabs,
         TextField, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadIcon from "@mui/icons-material/Upload";
import EventIcon from "@mui/icons-material/Event";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckIcon from "@mui/icons-material/Check";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import TableChartIcon from "@mui/icons-material/TableChart";
import api from "../../Auth/Api";
import globals from "../../Globals";
import { navigate, replaceDeep } from "../../Layout/Router";
import { subscribePoll } from "../../Layout/Poller";
import { PageContainer, SectionHeader, SelectorBar, SelectField, FormDialog, DataTable } from "../../Layout/Common";
import CountryPicker, { countryName } from "../../Layout/CountryPicker";
import { normalizeQuery } from "../PlayersPage";
import { ALL, ID_RE, FIXED, ellipsis, mono, Scroll, canEdit, canReadVersioning, asDate, utc, utcFull, toUtcInput, fromUtcInput, span,
         slotStatus, SlotChip, sortSlots, emptySlot, verLabel, verRange, clientPatchesOf, parseClient, slotVersionOptions, PlatformPicker,
         countryOptions, formatSize, checkBundleZip, UPLOAD_STATUS, uploadStatus, int } from "./liveops";

// LiveOps → Events (Evil Tower's event passes, the backend group `liveevents`): the DEFINITIONS (an event's screens
// bundle, its currency per level, the premium pass price, and since the database split (2026-09-24) its own TABLES -
// the mission pools and the prize tracks, imported from a sheet tab in the ExtraMissions layout or edited as JSON) and
// the SCHEDULE (slots: a UTC window + targeting by platform, client
// version and country; a player's progress keys on the slot), plus a player's progress (support). Everything is per
// branch; every write bumps the branch's events stamp, so players pick a change up at their next session sync.
// View needs `#liveops`, edits `liveops`. Dates are UTC.
// Deep link: /<game>/liveops/events/<definitions|schedule>[/<eventId|slotId>][/<branch> when not Development].
const TABS = [["definitions", "Definitions"], ["schedule", "Schedule"]];
const liveevents = (description, parts, options) => api.group("liveevents")(description, parts, options);
const REFRESH_MS = 60000;
const UPLOAD_TIMEOUT = 300000;   // the zip goes to the CDN bundle by bundle
const DIFFICULTY = ["easy", "medium", "hard"];

const emptyEvent = () => ({ eventId: "", title: "", currencyPerLevel: 20, premiumPriceGems: 0, autoOpen: true });
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
// an event's missions a day, easy/medium/hard
const perDay = (d) => (d ? `${d.missionsEasy ?? 0}/${d.missionsMedium ?? 0}/${d.missionsHard ?? 0}` : "?");
const tablesText = (t) => `${t.missions} missions + ${t.premiumMissions} premium, ${t.maxLevel} levels, free ${perDay(t.freeDaily)} a day, premium ${perDay(t.premiumDaily)} (easy/medium/hard)`;
// what the JSON editor starts from when an event has no tables yet (the ExtraMissions layout's keys)
const emptyTables = () => ({
    free: [{ missionId: "defeat_enemies", amount1: 10, amount2: 50, amount3: 100, eventCurrency1: 2, eventCurrency2: 3, eventCurrency3: 4, argumentFactor: "" }],
    premium: [], freeConfig: { missionsEasy: 1, missionsMedium: 0, missionsHard: 0 }, premiumConfig: { missionsEasy: 0, missionsMedium: 0, missionsHard: 0 },
    freePrizes: [{ step: 1, gems: 5 }], premiumPrizes: [],
});
const levelsText = (list) => ((list ?? []).length ? [...list].sort((a, b) => a - b).join(", ") : "none");
const muted = { color: "text.secondary" };

export default class EventsPage extends React.Component {
    loadToken = 0;
    state = {
        tab: "definitions", branch: "Development", data: null, loading: false, error: null,
        versionList: [],       // the versioning list (every branch): the version selects
        filter: ALL,           // the Schedule tab's event filter
        eventDialog: null,     // { mode: create | update, ev, busy, error }
        slotDialog: null,      // { mode: create | update, slot, busy, error }
        upload: null,          // { eventId, file, check, checkError, busy, error, result }
        tablesDialog: null,    // { eventId, mode: import | json, sheetId, tab, json, loading, busy, error, result }
        confirm: null,         // { kind: event | slot | reset, id, label, slots, slotId, busy, error }
        preview: null,         // { platform, client, clientText, country, at, busy, error, result }
        progress: { query: "", busy: false, error: null, result: null },   // the player progress tool (Schedule tab)
    };

    componentDidMount() {
        const deep = globals.route?.deep ?? [];
        const tab = TABS.some(([k]) => k === deep[0]) ? deep[0] : "definitions";
        const rest = deep.slice(1).filter(Boolean);
        const branch = rest.find(d => globals.branches.includes(d)) ?? "Development";
        const openId = rest.find(d => !globals.branches.includes(d)) ?? null;
        // sequenced: two requests in the same millisecond trip the backend's per-device anti-replay
        this.setState({ tab, branch }, () => { this.load(false, openId).finally(this.loadVersions); });
        this.unsubscribe = subscribePoll(() => this.load(true), REFRESH_MS);
    }

    componentWillUnmount() { this.unsubscribe?.(); }

    syncUrl = (id) => replaceDeep([this.state.tab, id ?? "", this.state.branch === "Development" ? "" : this.state.branch]);

    // ---------- loads ----------
    load = (poll = false, openId = null) => {
        const { branch } = this.state;
        const token = ++this.loadToken;
        if (!poll) this.setState({ loading: true, error: null });
        return liveevents("Loading events", ["list", branch], { silent: true })
            .then(r => {
                if (token !== this.loadToken) return true;
                this.setState({ data: r && typeof r === "object" ? r : null, loading: false, error: null });
                if (openId && r) this.openDeep(openId, r);
                return true;
            })
            .catch(err => {
                if (token === this.loadToken) this.setState(s => ({ loading: false, error: poll ? s.error : err.message }));
                return false;
            });
    }

    openDeep = (id, r) => {
        if (this.state.tab === "definitions") {
            const ev = (r.events ?? []).find(e => e.eventId === id);
            if (ev) this.openEvent(ev);
        } else {
            // a slot id opens it; an event id filters the schedule to that event
            const slot = (r.slots ?? []).find(s => s.slotId === id);
            if (slot) this.openSlot(slot);
            else if ((r.events ?? []).some(e => e.eventId === id)) this.setState({ filter: id });
        }
    }

    // The client versions of the version selects (no catalogue here); without the role only "any" and typed versions
    loadVersions = () => {
        if (!canReadVersioning()) { this.setState({ versionList: [] }); return Promise.resolve(); }
        return api.versioning("Loading versions", ["list"], { silent: true })
            .then(list => this.setState({ versionList: Array.isArray(list) ? list : [] }))
            .catch(() => this.setState({ versionList: [] }));
    }

    changeBranch = (branch) => {
        this.setState({ branch, data: null, filter: ALL }, () => { this.syncUrl(); this.load(); });
    }

    setTab = (_, tab) => this.setState({ tab }, () => this.syncUrl());

    // ---------- events ----------
    openNewEvent = () => this.setState({ eventDialog: { mode: "create", ev: emptyEvent(), busy: false, error: null } });

    openEvent = (e) => {
        this.setState({ eventDialog: { mode: "update", ev: { ...emptyEvent(), ...e, title: e.title ?? "" }, busy: false, error: null } });
        this.syncUrl(e.eventId);
    }

    closeEvent = () => { this.setState({ eventDialog: null }); this.syncUrl(); }

    setEvent = (patch) => this.setState(s => ({ eventDialog: s.eventDialog && { ...s.eventDialog, ev: { ...s.eventDialog.ev, ...patch }, error: null } }));

    saveEvent = () => {
        const { eventDialog: d, branch } = this.state;
        const e = d.ev, create = d.mode === "create";
        const perLevel = int(e.currencyPerLevel), price = int(e.premiumPriceGems);
        const problem = create && !ID_RE.test(e.eventId.trim()) ? "The event id: 1-40 letters, digits, '_' or '-'."
            : perLevel <= 0 ? "The currency per level must be positive: what one level of the reward track costs."
            : price < 0 ? "The pass price cannot be negative." : null;
        if (problem) { this.setState({ eventDialog: { ...d, error: problem } }); return; }
        const id = create ? e.eventId.trim() : e.eventId;
        // strict on the backend: exactly these keys
        const payload = { title: (e.title ?? "").trim(), currencyPerLevel: perLevel, premiumPriceGems: price, autoOpen: !!e.autoOpen };
        this.setState({ eventDialog: { ...d, busy: true, error: null } });
        liveevents(create ? `Creating event ${id}` : `Saving event ${id}`, [d.mode, branch, id], { payload })
            .then(() => { this.setState({ eventDialog: null }); this.syncUrl(); this.load(); })
            .catch(err => this.setState(s => ({ eventDialog: s.eventDialog && { ...s.eventDialog, busy: false, error: err.message } })));
    }

    scheduleEvent = (e) => this.setState({ tab: "schedule", filter: e.eventId }, () => { this.syncUrl(); this.openNewSlot(e.eventId); });

    // ---------- the tables (the event's own mission pools and prize tracks) ----------
    openTables = (e) => {
        this.setState({ tablesDialog: { eventId: e.eventId, mode: e.tables ? "json" : "import", sheetId: "", tab: "ExtraMissions", json: "", loading: true, busy: false, error: null, result: null } });
        liveevents(`Loading ${e.eventId}`, ["get", this.state.branch, e.eventId], { silent: true })
            .then(r => this.setTables({ json: JSON.stringify(r?.tables ?? emptyTables(), null, 2), loading: false }))
            .catch(err => this.setTables({ error: err.message, loading: false }));
    }

    closeTables = () => this.setState({ tablesDialog: null });
    setTables = (patch) => this.setState(s => ({ tablesDialog: s.tablesDialog && { ...s.tablesDialog, ...patch } }));

    importTables = () => {
        const { tablesDialog: t, branch } = this.state;
        const sheetId = (t.sheetId ?? "").trim(), tab = (t.tab ?? "").trim();
        if (!sheetId || !tab) { this.setTables({ error: "The spreadsheet and the tab are needed." }); return; }
        this.setTables({ busy: true, error: null, result: null });
        liveevents(`Importing the tables of ${t.eventId}`, ["import-tables", branch, t.eventId], { payload: { sheetId, tab } })
            .then(r => {
                const tb = r?.tables;
                this.setTables({ busy: false, result: tb ? `Imported: ${tablesText(tb)}; ${r.currencyPerLevel} currency per level.` : "Imported." });
                this.load();
                liveevents(`Loading ${t.eventId}`, ["get", branch, t.eventId], { silent: true }).then(g => this.setTables({ json: JSON.stringify(g?.tables ?? emptyTables(), null, 2) })).catch(() => null);
            })
            .catch(err => this.setTables({ busy: false, error: err.message }));
    }

    saveTablesJson = () => {
        const { tablesDialog: t, branch } = this.state;
        let obj;
        try { obj = JSON.parse(t.json); } catch (err) { this.setTables({ error: `Not valid JSON: ${err.message}` }); return; }
        this.setTables({ busy: true, error: null, result: null });
        liveevents(`Saving the tables of ${t.eventId}`, ["set-tables", branch, t.eventId], { payload: obj })
            .then(r => { this.setTables({ busy: false, result: r?.tables ? `Saved: ${tablesText(r.tables)}.` : "Saved." }); this.load(); })
            .catch(err => this.setTables({ busy: false, error: err.message }));
    }

    // ---------- the bundle ----------
    openUpload = (e) => this.setState({ upload: { eventId: e.eventId, file: null, check: null, checkError: null, busy: false, error: null, result: null } });

    pickZip = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        const { eventId } = this.state.upload;
        this.setState(s => ({ upload: { ...s.upload, file, check: null, checkError: null, error: null } }));
        checkBundleZip(file, eventId)
            .then(check => this.setState(s => (s.upload?.file === file ? { upload: { ...s.upload, check } } : null)))
            .catch(err => this.setState(s => (s.upload?.file === file ? { upload: { ...s.upload, checkError: err.message || String(err) } } : null)));
    }

    uploadZip = () => {
        const { upload: u, branch } = this.state;
        this.setState({ upload: { ...u, busy: true, error: null } });
        // a File payload carries no JSON body: every parameter is positional
        liveevents(`Uploading the bundle of ${u.eventId}`, ["upload-bundle", branch, u.eventId], { payload: u.file, timeout: UPLOAD_TIMEOUT })
            .then(result => { this.setState(s => ({ upload: s.upload && { ...s.upload, busy: false, result } })); this.load(); })
            .catch(err => this.setState(s => ({ upload: s.upload && { ...s.upload, busy: false, error: err.message } })));
    }

    // ---------- slots ----------
    openNewSlot = (key) => {
        const { filter, data } = this.state;
        const events = data?.events ?? [];
        const pick = key ?? (filter !== ALL && events.some(e => e.eventId === filter) ? filter : events[0]?.eventId ?? "");
        this.setState({ slotDialog: { mode: "create", slot: emptySlot(pick), busy: false, error: null } });
    }

    openSlot = (s) => {
        this.setState({ slotDialog: { mode: "update", slot: { ...s, platforms: [...(s.platforms ?? [])], countries: [...(s.countries ?? [])], note: s.note ?? "" }, busy: false, error: null } });
        this.syncUrl(s.slotId);
    }

    closeSlot = () => { this.setState({ slotDialog: null }); this.syncUrl(); }

    setSlot = (patch) => this.setState(s => ({ slotDialog: s.slotDialog && { ...s.slotDialog, slot: { ...s.slotDialog.slot, ...patch }, error: null } }));

    saveSlot = () => {
        const { slotDialog: d, branch } = this.state;
        const s = d.slot;
        const start = asDate(s.start), end = asDate(s.end);
        const problem = !s.key ? "Pick the event."
            : !start || !end ? "Start and end are required."
            : end <= start ? "The end must be after the start."
            : s.minVersion && s.maxVersion && s.maxVersion < s.minVersion ? "The max version is below the min version." : null;
        if (problem) { this.setState({ slotDialog: { ...d, error: problem } }); return; }
        const payload = {
            key: s.key, start: start.toISOString(), end: end.toISOString(), platforms: s.platforms ?? [],
            minVersion: s.minVersion ?? 0, maxVersion: s.maxVersion ?? 0, countries: s.countries ?? [], enabled: !!s.enabled, note: (s.note ?? "").trim(),
        };
        const create = d.mode === "create";
        this.setState({ slotDialog: { ...d, busy: true, error: null } });
        liveevents(create ? `Scheduling ${s.key}` : "Saving the slot", create ? ["slot-create", branch] : ["slot-update", branch, s.slotId], { payload })
            .then(() => { this.setState({ slotDialog: null }); this.syncUrl(); this.load(); })
            .catch(err => this.setState(st => ({ slotDialog: st.slotDialog && { ...st.slotDialog, busy: false, error: err.message } })));
    }

    versionOptions(kind, current) { return slotVersionOptions(this.state.versionList, this.state.branch, kind, current); }

    // ---------- deletes / the progress reset ----------
    confirmAction = () => {
        const { confirm: c, branch } = this.state;
        const call = c.kind === "event" ? [`Deleting event ${c.id}`, ["delete", branch, c.id]]
            : c.kind === "slot" ? ["Deleting the slot", ["slot-delete", branch, c.id]]
            : ["Resetting the event progress", ["reset-progress", branch, c.id, ...(c.slotId ? [c.slotId] : [])]];
        this.setState({ confirm: { ...c, busy: true, error: null } });
        liveevents(call[0], call[1])
            .then(() => { this.setState({ confirm: null }); if (c.kind === "reset") this.loadProgress(); else this.load(); })
            .catch(err => this.setState(s => ({ confirm: s.confirm && { ...s.confirm, busy: false, error: err.message } })));
    }

    // ---------- preview / player progress ----------
    openPreview = () => {
        const versions = clientPatchesOf(this.state.versionList, this.state.branch);
        this.setState({ preview: { platform: globals.platformNames[0], client: versions[0]?.value ?? "", clientText: "", country: "", at: "", busy: false, error: null, result: null } });
    }

    previewCompact() {
        const { preview: p, branch } = this.state;
        if (!p) return null;
        return p.clientText.trim() ? parseClient(p.clientText, branch) : (p.client === "" ? null : p.client);
    }

    runPreview = () => {
        const { preview: p, branch } = this.state;
        const compact = this.previewCompact();
        if (compact == null) return;
        const at = fromUtcInput(p.at);
        const parts = ["preview", branch, p.platform, compact];
        // positional: a time without a country passes "-" (matches no country-targeted slot, like no country at all)
        if (p.country || at) parts.push(p.country || "-");
        if (at) parts.push(at);
        this.setState({ preview: { ...p, busy: true, error: null } });
        liveevents("Previewing", parts, { silent: true })
            .then(result => this.setState(s => ({ preview: s.preview && { ...s.preview, busy: false, result: { ...result, compact } } })))
            .catch(err => this.setState(s => ({ preview: s.preview && { ...s.preview, busy: false, error: err.message, result: null } })));
    }

    setProgress = (patch) => this.setState(s => ({ progress: { ...s.progress, ...patch } }));

    // The player as the Players page takes it: a link ("7K3QD-M9X2A", forgiving) or a 24-hex player id
    loadProgress = () => {
        const q = normalizeQuery(this.state.progress.query);
        if (!q) return;
        this.setProgress({ busy: true, error: null });
        liveevents("Loading the event progress", ["progress", this.state.branch, q], { silent: true })
            .then(r => this.setProgress({ busy: false, result: r && typeof r === "object" ? r : null }))
            .catch(err => this.setProgress({ busy: false, error: err.message, result: null }));
    }

    // ---------- render: tables ----------
    // Every event holds its own mission pools and prize tracks (Tables on the event); one without them rolls nothing
    renderSheet() {
        const { data } = this.state;
        if (!data) return null;
        const missing = (data.events ?? []).filter(e => !e.tables).map(e => e.eventId);
        if (missing.length === 0) return null;
        return <Alert severity="warning" sx={{ mb: 2 }}>{plural(missing.length, "event")} without tables ({missing.join(", ")}): {missing.length === 1 ? "it rolls" : "they roll"} no missions and {missing.length === 1 ? "pays" : "pay"} no prizes until Tables is set (import a sheet tab or edit the JSON).</Alert>;
    }

    renderDefinitions() {
        const { data, loading, branch } = this.state;
        const edit = canEdit();
        const events = data?.events ?? [];
        const now = Date.now();
        if (!data) return null;
        if (events.length === 0) {
            return <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{loading ? "Loading…" : `No events on ${branch} yet.${edit ? " Create one with New event." : ""}`}</Typography>;
        }
        const upcoming = new Set((data.slots ?? []).filter(s => slotStatus(s, now) === "upcoming").map(s => s.key));
        const columns = [
            { id: "eventId", label: "Event", width: 170, sx: ellipsis, render: e => <span title={e.eventId} style={mono}>{e.eventId}</span> },
            { id: "title", label: "Title", width: 200, sx: ellipsis, render: e => <span title={e.title}>{e.title || "—"}</span> },
            { id: "bundle", label: "Bundle", width: 170, sx: ellipsis, render: e => (e.bundle ? <span title={e.bundle} style={mono}>{e.bundle}</span> : "—") },
            { id: "tables", label: "Tables", width: 210, sx: ellipsis, render: e => (e.tables
                ? <Tooltip title={tablesText(e.tables)}><span>{e.tables.missions}+{e.tables.premiumMissions} missions · {e.tables.maxLevel} levels</span></Tooltip>
                : <Box component="span" sx={{ color: "warning.main" }}>none</Box>) },
            { id: "perLevel", label: "Currency/level", width: 115, align: "right", render: e => (e.currencyPerLevel > 0 ? e.currencyPerLevel
                : <Box component="span" sx={{ color: "warning.main" }} title="Set it on the event: what one reward level costs">not set</Box>) },
            { id: "price", label: "Pass price", width: 115, align: "right", render: e => (e.premiumPriceGems > 0 ? `${e.premiumPriceGems} gems`
                : <Box component="span" sx={muted}>not for sale</Box>) },
            { id: "autoOpen", label: "Auto open", width: 85, render: e => (e.autoOpen ? "yes" : "no") },
            { id: "slots", label: "Slots", width: 60, align: "right", render: e => e.slots },
            { id: "live", label: "Live", width: 100, render: e => (e.live ? <SlotChip status="live" /> : upcoming.has(e.eventId) ? <SlotChip status="upcoming" /> : "—") },
            { id: "updated", label: "Updated (UTC)", width: 140, render: e => <Tooltip title={`v${e.version} · ${e.updatedBy ?? ""}`}><span>{utc(e.updatedDate)}</span></Tooltip> },
            { id: "actions", label: "", width: 180, align: "right", render: e => (
                <Box sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title={edit ? "Edit" : "View"}><IconButton size="small" onClick={() => this.openEvent(e)}>{edit ? <EditIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}</IconButton></Tooltip>
                    <Tooltip title={edit ? "Tables (missions, prize tracks)" : "Tables"}><IconButton size="small" onClick={() => this.openTables(e)}><TableChartIcon fontSize="small" /></IconButton></Tooltip>
                    {edit && <>
                        <Tooltip title="Upload bundle"><IconButton size="small" onClick={() => this.openUpload(e)}><UploadIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Schedule"><IconButton size="small" onClick={() => this.scheduleEvent(e)}><EventIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" onClick={() => this.setState({ confirm: { kind: "event", id: e.eventId, slots: e.slots, busy: false, error: null } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </>}
                </Box>
            )},
        ];
        return <DataTable sx={FIXED} columns={columns} rows={events} getRowKey={e => e.eventId} />;
    }

    renderSchedule(filter) {
        const { data, loading, branch } = this.state;
        const edit = canEdit();
        if (!data) return null;
        const now = Date.now();
        const titles = new Map((data.events ?? []).map(e => [e.eventId, e.title]));
        const rows = sortSlots((data.slots ?? []).filter(s => filter === ALL || s.key === filter), now);
        if (rows.length === 0) {
            return <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{loading ? "Loading…" : `Nothing scheduled on ${branch}${filter === ALL ? "" : ` for ${filter}`}.`}</Typography>;
        }
        const columns = [
            { id: "event", label: "Event", width: 170, sx: ellipsis, render: s => (
                <span title={titles.has(s.key) ? `${s.key} · ${titles.get(s.key) || ""}` : `${s.key} (no such event)`} style={mono}>{s.key}</span>
            )},
            { id: "window", label: "Window (UTC)", width: 270, render: s => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, whiteSpace: "nowrap" }}>
                    <SlotChip status={slotStatus(s, now)} />
                    <Tooltip title={`${utcFull(s.start)} → ${utcFull(s.end)}`}><span>{utc(s.start)} → {utc(s.end)}</span></Tooltip>
                </Box>
            )},
            { id: "platforms", label: "Platforms", width: 150, sx: ellipsis, render: s => ((s.platforms ?? []).length
                ? <span title={s.platforms.join(", ")}>{s.platforms.map(p => <Chip key={p} size="small" label={p} sx={{ mr: 0.5, height: 20 }} />)}</span> : "all") },
            { id: "versions", label: "Versions", width: 150, sx: ellipsis, render: s => verRange(s.minVersion, s.maxVersion) },
            { id: "countries", label: "Countries", width: 150, sx: ellipsis, render: s => ((s.countries ?? []).length
                ? <span title={s.countries.map(c => `${countryName(c)} (${c})`).join(", ")}>{s.countries.join(", ")}</span> : "all") },
            { id: "note", label: "Note", width: 220, sx: ellipsis, render: s => <span title={s.note ?? ""}>{s.note ?? ""}</span> },
            { id: "updated", label: "Updated (UTC)", width: 140, render: s => <Tooltip title={s.updatedBy ?? ""}><span>{utc(s.updatedDate)}</span></Tooltip> },
            { id: "actions", label: "", width: 100, align: "right", render: s => (
                <Box sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title={edit ? "Edit" : "View"}><IconButton size="small" onClick={() => this.openSlot(s)}>{edit ? <EditIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}</IconButton></Tooltip>
                    {edit && <Tooltip title="Delete"><IconButton size="small" onClick={() => this.setState({ confirm: { kind: "slot", id: s.slotId, label: `${s.key} ${utc(s.start)} → ${utc(s.end)}`, busy: false, error: null } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
                </Box>
            )},
        ];
        return <DataTable sx={FIXED} columns={columns} rows={rows} getRowKey={s => s.slotId} />;
    }

    // ---------- render: the player progress tool ----------
    renderMissions(list) {
        if (!(list ?? []).length) return <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>No missions rolled.</Typography>;
        return (
            <DataTable sx={{ ...FIXED, mt: 0.5 }} rows={list} getRowKey={(m, i) => `${m.missionId}/${i}`} columns={[
                { id: "missionId", label: "Mission", width: 200, sx: ellipsis, render: m => <span title={m.missionId} style={mono}>{m.missionId}</span> },
                { id: "difficulty", label: "Difficulty", width: 90, render: m => DIFFICULTY[m.difficulty] ?? m.difficulty },
                { id: "progress", label: "Progress", width: 110, align: "right", render: m => `${m.progress ?? 0}/${m.target ?? 0}` },
                { id: "reward", label: "Reward", width: 80, align: "right" },
                { id: "collected", label: "Collected", width: 80, align: "center", render: m => (m.collected ? <CheckIcon fontSize="small" color="success" sx={{ verticalAlign: "middle" }} /> : "—") },
            ]} />
        );
    }

    renderProgressEvent(ev, playerId) {
        const { data } = this.state;
        const edit = canEdit();
        const title = (data?.events ?? []).find(e => e.eventId === ev.eventId)?.title;
        const maxLevel = (data?.events ?? []).find(e => e.eventId === ev.eventId)?.tables?.maxLevel ?? null;
        const pass = ev.premiumPurchased ? <Chip size="small" color="success" label="pass owned" sx={{ height: 20 }} />
            : ev.premiumPriceGems > 0 ? `pass not bought (${ev.premiumPriceGems} gems)` : "pass not for sale";
        return (
            <Box key={ev.slotId} sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                    <SlotChip status={slotStatus({ enabled: true, start: ev.start, end: ev.end })} />
                    <Box component="span" sx={{ ...mono, fontSize: 14, fontWeight: 600 }}>{ev.eventId}</Box>
                    {title && <Typography variant="body2">{title}</Typography>}
                    <Tooltip title={`slot ${ev.slotId}`}><Typography variant="body2" color="text.secondary">{utc(ev.start)} → {utc(ev.end)} UTC</Typography></Tooltip>
                    <Box sx={{ flex: 1 }} />
                    {edit && <Button size="small" color="warning" startIcon={<RestartAltIcon />}
                                     onClick={() => this.setState({ confirm: { kind: "reset", id: playerId, slotId: ev.slotId, label: ev.eventId, busy: false, error: null } })}>
                        Reset this event
                    </Button>}
                </Box>
                <Typography variant="body2" component="div" sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <span>level {ev.level}{maxLevel ? ` of ${maxLevel}` : ""} · {ev.currency} currency ({ev.currencyPerLevel}/level) ·</span>
                    {pass}
                    <span>· claimed free: {levelsText(ev.freeClaimed)} · premium: {levelsText(ev.premiumClaimed)} · next reset {utcFull(ev.nextReset)} · rolls {ev.rolls ?? 0}</span>
                </Typography>
                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 1 }}>
                    <Box sx={{ width: 560, maxWidth: "100%" }}>
                        <Typography variant="caption" color="text.secondary">Free · today's missions</Typography>
                        {this.renderMissions(ev.free)}
                    </Box>
                    <Box sx={{ width: 560, maxWidth: "100%" }}>
                        <Typography variant="caption" color="text.secondary">Premium · today's missions{ev.premiumPurchased ? "" : " (count only with the pass)"}</Typography>
                        {this.renderMissions(ev.premium)}
                    </Box>
                </Box>
            </Box>
        );
    }

    renderProgress() {
        const { progress: pr } = this.state;
        const edit = canEdit();
        const r = pr.result, events = r?.events ?? [];
        const openPlayer = (id) => navigate({ title: globals.selectedTitle, page: "home", sub: "players", deep: [id] });
        return (
            <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <Typography variant="h6">Player progress</Typography>
                    <TextField size="small" label="Player id or link" sx={{ width: 280 }} value={pr.query}
                               onChange={e => this.setProgress({ query: e.target.value })} onKeyDown={e => { if (e.key === "Enter") this.loadProgress(); }} />
                    <Button variant="outlined" size="small" onClick={this.loadProgress} disabled={pr.busy || !normalizeQuery(pr.query)}>Load</Button>
                    {r && <Typography variant="body2" color="text.secondary">
                        player <Link component="button" variant="body2" sx={mono} onClick={() => openPlayer(r.playerId)}>{r.playerId}</Link> · {plural(events.length, "event")}
                    </Typography>}
                    <Box sx={{ flex: 1 }} />
                    {edit && r && events.length > 0 && (
                        <Button size="small" variant="outlined" color="warning" startIcon={<RestartAltIcon />}
                                onClick={() => this.setState({ confirm: { kind: "reset", id: r.playerId, slotId: null, label: plural(events.length, "event"), busy: false, error: null } })}>
                            Reset progress
                        </Button>
                    )}
                </Box>
                {pr.busy && <LinearProgress sx={{ mt: 2 }} />}
                {pr.error && <Alert severity="error" sx={{ mt: 2 }}>{pr.error}</Alert>}
                {r && events.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        No event progress on this account: an event is rolled at the player's first sync inside a live slot that targets them.
                    </Typography>
                )}
                {events.map(ev => this.renderProgressEvent(ev, r.playerId))}
            </Paper>
        );
    }

    // ---------- render: dialogs ----------
    renderEventDialog() {
        const { eventDialog: d, branch } = this.state;
        if (!d) return null;
        const e = d.ev, create = d.mode === "create", edit = canEdit();
        const badId = create && !!e.eventId && !ID_RE.test(e.eventId.trim());
        const num = (label, field, helperText) => (
            <TextField label={label} type="number" size="small" sx={{ width: 230 }} value={e[field]} disabled={!edit} helperText={helperText}
                       slotProps={{ htmlInput: { min: 0 } }} onChange={ev => this.setEvent({ [field]: ev.target.value })} />
        );
        return (
            <FormDialog open title={create ? `New event · ${branch}` : `${edit ? "Edit" : "Event"} ${e.eventId} · ${branch}`} onClose={this.closeEvent} onConfirm={this.saveEvent}
                        confirmText={create ? "Create" : "Save"} hideConfirm={!edit} cancelText={edit ? "Cancel" : "Close"}
                        confirmDisabled={d.busy || badId} cancelDisabled={d.busy} maxWidth="md" fullWidth>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    {d.error && <Alert severity="error">{d.error}</Alert>}
                    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                        <TextField label="Event id" size="small" value={e.eventId} disabled={!create} error={badId} sx={{ width: 300, flexShrink: 0 }}
                                   helperText={create ? "Letters, digits, _ and -, max 40: also the dialog ids and the Unity folder Assets/LiveOps/<id>" : `Unity folder Assets/LiveOps/${e.eventId}`}
                                   onChange={ev => this.setEvent({ eventId: ev.target.value })} />
                        <TextField label="Title" size="small" fullWidth value={e.title ?? ""} disabled={!edit} helperText="The operator's label"
                                   onChange={ev => this.setEvent({ title: ev.target.value })} />
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
                        {num("Currency per level", "currencyPerLevel", "What one level of the reward track costs (required)")}
                        {num("Premium pass price (gems)", "premiumPriceGems", "0 = the pass cannot be bought")}
                        <FormControlLabel label="Auto open (once per app run)" disabled={!edit} sx={{ mt: 0.5 }}
                                          control={<Switch checked={!!e.autoOpen} onChange={ev => this.setEvent({ autoOpen: ev.target.checked })} />} />
                    </Box>
                    {int(e.premiumPriceGems) <= 0 && (
                        <Alert severity="info">Price 0: the premium pass is not for sale - players only get the free track.</Alert>
                    )}
                    {!create && (
                        <Typography variant="body2" color="text.secondary">
                            Tables: {e.tables ? tablesText(e.tables) : <Box component="span" sx={{ color: "warning.main" }}>none - the event rolls no missions (Tables on the event)</Box>}
                            <br />Bundle: {e.bundle ? <Box component="span" sx={mono}>{e.bundle}</Box> : `none yet - upload the LiveOpsBundleBuild zip of Assets/LiveOps/${e.eventId}`}
                            {" · "}{plural(e.slots ?? 0, "slot")} · v{e.version} · updated {utcFull(e.updatedDate)} by {e.updatedBy || "?"}
                        </Typography>
                    )}
                </Box>
            </FormDialog>
        );
    }

    renderTablesDialog() {
        const { tablesDialog: t, branch } = this.state;
        if (!t) return null;
        const edit = canEdit();
        const isImport = t.mode === "import";
        return (
            <FormDialog open title={`Tables · ${t.eventId} · ${branch}`} onClose={this.closeTables} onConfirm={isImport ? this.importTables : this.saveTablesJson}
                        confirmText={isImport ? "Import" : "Save"} hideConfirm={!edit} cancelText={edit ? "Cancel" : "Close"}
                        confirmDisabled={t.busy || t.loading || (isImport ? !(t.sheetId ?? "").trim() || !(t.tab ?? "").trim() : !t.json)} cancelDisabled={t.busy} maxWidth="md" fullWidth>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    {t.error && <Alert severity="error">{t.error}</Alert>}
                    {t.result && <Alert severity="success">{t.result}</Alert>}
                    <Tabs value={t.mode} onChange={(_, v) => this.setTables({ mode: v, error: null, result: null })}>
                        <Tab value="import" label="Import from a sheet tab" />
                        <Tab value="json" label="JSON" />
                    </Tabs>
                    {isImport ? <>
                        <Typography variant="body2" color="text.secondary">
                            A tab in the ExtraMissions layout (the tables extraMissions, extraMissionsPremium, extraMissionsConfig, extraMissionsPremiumConfig,
                            extraMissionsCommonPrize, extraMissionsPremiumPrize; the EvilT - Backend workbook keeps one as the template). Read once, now: the event keeps
                            its own copy, and the config row's currencyPerLevel is copied onto the event.
                        </Typography>
                        <TextField label="Spreadsheet id or URL" size="small" fullWidth value={t.sheetId} disabled={!edit || t.busy} onChange={ev => this.setTables({ sheetId: ev.target.value })} />
                        <TextField label="Tab name" size="small" sx={{ width: 300 }} value={t.tab} disabled={!edit || t.busy} onChange={ev => this.setTables({ tab: ev.target.value })} />
                    </> : (
                        <TextField label="Tables (JSON)" multiline minRows={16} maxRows={32} fullWidth value={t.loading ? "Loading…" : t.json} disabled={!edit || t.busy || t.loading}
                                   slotProps={{ htmlInput: { style: { fontFamily: "monospace", fontSize: 12 } } }} onChange={ev => this.setTables({ json: ev.target.value })} />
                    )}
                </Box>
            </FormDialog>
        );
    }

    renderSlotDialog() {
        const { slotDialog: d, data, branch } = this.state;
        if (!d) return null;
        const s = d.slot, create = d.mode === "create", edit = canEdit();
        const events = data?.events ?? [];
        const eventOptions = events.map(e => ({ value: e.eventId, label: e.title ? `${e.eventId} · ${e.title}` : e.eventId }));
        if (s.key && !events.some(e => e.eventId === s.key)) eventOptions.push({ value: s.key, label: `${s.key} (no such event)` });
        const start = asDate(s.start), end = asDate(s.end);
        const length = start && end && end > start ? `lasts ${span(end - start)}` : "typed in UTC";
        return (
            <FormDialog open title={create ? `New slot · ${branch}` : `${edit ? "Edit slot" : "Slot"} · ${s.key} · ${branch}`} onClose={this.closeSlot} onConfirm={this.saveSlot}
                        confirmText={create ? "Schedule" : "Save"} hideConfirm={!edit} cancelText={edit ? "Cancel" : "Close"}
                        confirmDisabled={d.busy} cancelDisabled={d.busy} maxWidth="md" fullWidth>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    {d.error && <Alert severity="error">{d.error}</Alert>}
                    <SelectField label="Event" size="small" fullWidth value={s.key} options={eventOptions} disabled={!edit} onChange={v => this.setSlot({ key: v })} />
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
                        <TextField label="Start (UTC)" type="datetime-local" size="small" value={toUtcInput(s.start)} disabled={!edit} sx={{ width: 230 }}
                                   slotProps={{ inputLabel: { shrink: true } }} helperText={`now ${utcFull(new Date().toISOString())}`}
                                   onChange={e => this.setSlot({ start: fromUtcInput(e.target.value) })} />
                        <TextField label="End (UTC)" type="datetime-local" size="small" value={toUtcInput(s.end)} disabled={!edit} sx={{ width: 230 }}
                                   slotProps={{ inputLabel: { shrink: true } }} helperText={length}
                                   onChange={e => this.setSlot({ end: fromUtcInput(e.target.value) })} />
                        <FormControlLabel label="Enabled" disabled={!edit} sx={{ mt: 0.5 }}
                                          control={<Switch checked={!!s.enabled} onChange={e => this.setSlot({ enabled: e.target.checked })} />} />
                    </Box>
                    <PlatformPicker value={s.platforms} disabled={!edit} onChange={v => this.setSlot({ platforms: v })} />
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <SelectField label="Min version" size="small" minWidth={220} value={s.minVersion ?? 0} options={this.versionOptions("min", s.minVersion)} disabled={!edit}
                                     onChange={v => this.setSlot({ minVersion: v })} />
                        <SelectField label="Max version" size="small" minWidth={220} value={s.maxVersion ?? 0} options={this.versionOptions("max", s.maxVersion)} disabled={!edit}
                                     onChange={v => this.setSlot({ maxVersion: v })} />
                        {this.state.versionList.length === 0 && <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>No version list here: only “any”.</Typography>}
                    </Box>
                    <CountryPicker value={s.countries ?? []} onChange={v => (edit ? this.setSlot({ countries: v }) : null)} />
                    <TextField label="Note" size="small" fullWidth value={s.note ?? ""} disabled={!edit} onChange={e => this.setSlot({ note: e.target.value })} />
                    {!create && <Typography variant="caption" color="text.secondary">Slot {s.slotId} · updated {utcFull(s.updatedDate)} by {s.updatedBy || "?"}. Players' progress keys on the slot: editing the window keeps it.</Typography>}
                </Box>
            </FormDialog>
        );
    }

    renderUploadDialog() {
        const { upload: u, branch } = this.state;
        if (!u) return null;
        const { check, result } = u;
        const blocked = !!check && (!check.hasOwn || check.missing.length > 0);
        const byPlatform = (a, b) => (a.platform ?? "").localeCompare(b.platform ?? "") || (a.name ?? "").localeCompare(b.name ?? "");
        const reg = result?.registered;
        const rows = reg ? [...(reg.bundles ?? [])].map(b => ({ ...b, status: uploadStatus(b) })).sort(byPlatform) : [];
        const counts = rows.reduce((acc, r) => { acc[r.status] = (acc[r.status] ?? 0) + 1; return acc; }, {});
        return (
            <FormDialog open title={result ? `Bundle uploaded · ${u.eventId}` : `Upload the bundle of ${u.eventId} · ${branch}`}
                        onClose={() => this.setState({ upload: null })} maxWidth={false} PaperProps={{ sx: { width: 900, maxWidth: "95vw" } }}
                        confirmText="Upload" onConfirm={this.uploadZip} hideConfirm={!!result} cancelText={result ? "Close" : "Cancel"}
                        confirmDisabled={!u.file || u.busy || blocked} cancelDisabled={u.busy}>
                {!result && <>
                    <Typography variant="body2" color="text.secondary">
                        The zip Unity's LiveOpsBundleBuild makes of Assets/LiveOps/{u.eventId}: every platform's bundles, registered on {branch} under the
                        owner event:{u.eventId}; the event then points at liveops/{u.eventId}. Players re-read the event at their next sync.
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 2 }}>
                        <Button variant="outlined" component="label" disabled={u.busy}>
                            Choose zip
                            <input type="file" accept=".zip" hidden onChange={this.pickZip} />
                        </Button>
                        {u.file && <Typography variant="body2" sx={ellipsis}>{u.file.name} · {formatSize(u.file.size)}</Typography>}
                    </Box>
                    {u.file && !check && !u.checkError && <LinearProgress sx={{ mt: 2 }} />}
                    {u.checkError && <Alert severity="warning" sx={{ mt: 2 }}>Could not read the zip here: {u.checkError} The server checks it again.</Alert>}
                    {check && !check.hasOwn && <Alert severity="error" sx={{ mt: 2 }}>The zip carries no '{check.expected}' bundle (it has {check.names.join(", ")}): build it from Assets/LiveOps/{u.eventId}.</Alert>}
                    {check && check.missing.length > 0 && <Alert severity="error" sx={{ mt: 2 }}>Files missing from the zip: {check.missing.join(", ")}</Alert>}
                    {check && check.owner && check.owner !== `event:${u.eventId}` &&
                        <Alert severity="info" sx={{ mt: 2 }}>The manifest names the owner {check.owner}; this upload registers it as event:{u.eventId}.</Alert>}
                    {check && <>
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            owner {check.owner || "?"}{check.unity ? ` · Unity ${check.unity}` : ""}{check.changeset ? ` · cs:${check.changeset}` : ""}
                            {" · "}{check.bundles.length} bundles{check.platforms.length ? ` · ${check.expected} for ${check.platforms.join(", ")}` : ""}
                        </Typography>
                        <Scroll>
                            <DataTable sx={FIXED} rows={[...check.bundles].sort(byPlatform)} getRowKey={(b, i) => `${b.platform}/${b.name}/${i}`} columns={[
                                { id: "platform", label: "Platform", width: 90 },
                                { id: "name", label: "Name", width: 300, sx: ellipsis, render: b => <span title={b.name} style={b.name === check.expected ? { fontWeight: 600 } : undefined}>{b.name}</span> },
                                { id: "size", label: "Size", width: 90, align: "right", render: b => formatSize(b.size) },
                                { id: "deps", label: "Deps", width: 330, sx: ellipsis, render: b => { const t = (b.deps ?? []).join(", "); return <span title={t}>{t}</span>; } },
                            ]} />
                        </Scroll>
                    </>}
                </>}
                {result && <>
                    <Typography variant="body2">
                        {result.bundle} · owner {reg?.owner ?? "?"} · {Object.entries(counts).map(([k, n]) => `${n} ${UPLOAD_STATUS[k].label}`).join(" · ") || "no bundles"}
                        {" · "}seq {Object.entries(reg?.seq ?? {}).map(([p, n]) => `${p} ${n}`).join(", ") || "-"}
                    </Typography>
                    <Scroll>
                        <DataTable sx={FIXED} rows={rows} getRowKey={(b, i) => `${b.platform}/${b.name}/${i}`} columns={[
                            { id: "platform", label: "Platform", width: 90 },
                            { id: "name", label: "Name", width: 240, sx: ellipsis, render: b => <span title={b.name}>{b.name}</span> },
                            { id: "status", label: "", width: 110, render: b => <Chip size="small" variant="outlined" color={UPLOAD_STATUS[b.status].color} label={UPLOAD_STATUS[b.status].label} /> },
                            { id: "version", label: "Ver", width: 60, align: "right" },
                            { id: "size", label: "Size", width: 90, align: "right", render: b => formatSize(b.size) },
                            { id: "oldSize", label: "Was", width: 90, align: "right", render: b => (b.oldSize ? formatSize(b.oldSize) : "") },
                            { id: "error", label: "Error", width: 150, sx: ellipsis, render: b => <span title={b.error ?? ""}>{b.error ?? ""}</span> },
                        ]} />
                    </Scroll>
                </>}
                {u.busy && <LinearProgress sx={{ mt: 2 }} />}
                {u.error && <Alert severity="error" sx={{ mt: 2 }}>{u.error}</Alert>}
            </FormDialog>
        );
    }

    renderConfirmDialog() {
        const { confirm: c, branch } = this.state;
        if (!c) return null;
        const title = c.kind === "event" ? `Delete event ${c.id}?` : c.kind === "slot" ? "Delete this slot?" : c.slotId ? `Reset the progress in ${c.label}?` : "Reset the event progress?";
        const text = c.kind === "event"
            ? `Removes ${c.id} from ${branch} and its ${plural(c.slots ?? 0, "slot")} from the schedule: players stop seeing it at their next sync.`
            : c.kind === "slot" ? `${c.label} on ${branch}: players stop seeing it at their next sync.`
            : `Drops the progress of player ${c.id} in ${c.slotId ? `${c.label} (slot ${c.slotId})` : `every event (${c.label})`}: currency, claimed levels and `
              + "today's missions. The event starts over at the player's next sync. Internal/Development accounts only.";
        return (
            <FormDialog open title={title} onClose={() => this.setState({ confirm: null })}
                        confirmText={c.kind === "reset" ? "Reset" : "Delete"} onConfirm={this.confirmAction} confirmDisabled={c.busy} cancelDisabled={c.busy}>
                <Typography variant="body2">{text}</Typography>
                {c.busy && <LinearProgress sx={{ mt: 2 }} />}
                {c.error && <Alert severity="error" sx={{ mt: 2 }}>{c.error}</Alert>}
            </FormDialog>
        );
    }

    renderPreviewDialog() {
        const { preview: p, branch, versionList, data } = this.state;
        if (!p) return null;
        const versions = clientPatchesOf(versionList, branch);
        const compact = this.previewCompact();
        const r = p.result, visible = r?.visible ?? [];
        const at = asDate(r?.at)?.getTime();
        const titles = new Map((data?.events ?? []).map(e => [e.eventId, e.title]));
        const set = (field) => (value) => this.setState(s => ({ preview: { ...s.preview, [field]: value } }));
        return (
            <FormDialog open title={`Preview for a player · ${branch}`} onClose={() => this.setState({ preview: null })}
                        maxWidth={false} PaperProps={{ sx: { width: 980, maxWidth: "95vw" } }} cancelText="Close"
                        confirmText="Preview" onConfirm={this.runPreview} confirmDisabled={compact == null || p.busy}>
                <Typography variant="body2" color="text.secondary">
                    The event slots a player with these facts is told about: live ones, those starting within 7 days and those ended
                    less than 3 days ago (claims still allowed), targeting applied. Empty time = now.
                </Typography>
                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", mt: 2, flexWrap: "wrap" }}>
                    <SelectField label="Platform" size="small" minWidth={130} value={p.platform} options={globals.platformNames} onChange={set("platform")} />
                    {versions.length > 0 && <SelectField label="Client version" size="small" minWidth={150} value={p.client} options={versions}
                                                         onChange={set("client")} disabled={!!p.clientText.trim()} />}
                    <TextField size="small" label={versions.length ? "or type M.m.p" : "Client version (M.m.p)"} sx={{ width: 170 }}
                               value={p.clientText} onChange={e => set("clientText")(e.target.value)} error={!!p.clientText.trim() && compact == null} />
                    <Autocomplete size="small" options={countryOptions()} value={p.country || null} sx={{ width: 240 }}
                                  onChange={(e, v) => set("country")(v ?? "")} getOptionLabel={c => `${countryName(c)} (${c})`}
                                  renderInput={params => <TextField {...params} label="Country" placeholder="none" />} />
                    <TextField label="At (UTC)" type="datetime-local" size="small" value={p.at} sx={{ width: 220 }}
                               slotProps={{ inputLabel: { shrink: true } }} onChange={e => set("at")(e.target.value)} />
                </Box>
                {p.busy && <LinearProgress sx={{ mt: 2 }} />}
                {p.error && <Alert severity="error" sx={{ mt: 2 }}>{p.error}</Alert>}
                {r && <>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        At {utcFull(r.at)}, {p.platform} {verLabel(r.compact)}{p.country ? ` in ${countryName(p.country)}` : ""}: {plural(visible.length, "slot")}
                    </Typography>
                    {visible.length > 0 && (
                        <DataTable sx={{ ...FIXED, mt: 1 }} rows={visible} getRowKey={v => v.slot.slotId} columns={[
                            { id: "status", label: "", width: 100, render: v => <SlotChip status={v.live ? "live" : slotStatus({ ...v.slot, enabled: true }, at)} /> },
                            { id: "event", label: "Event", width: 200, sx: ellipsis, render: v => <span title={titles.get(v.ev) ?? ""} style={mono}>{v.ev ?? v.slot.key}</span> },
                            { id: "window", label: "Window (UTC)", width: 230, render: v => `${utc(v.slot.start)} → ${utc(v.slot.end)}` },
                            { id: "versions", label: "Versions", width: 150, sx: ellipsis, render: v => verRange(v.slot.minVersion, v.slot.maxVersion) },
                            { id: "note", label: "Note", width: 220, sx: ellipsis, render: v => <span title={v.slot.note ?? ""}>{v.slot.note ?? ""}</span> },
                        ]} />
                    )}
                </>}
            </FormDialog>
        );
    }

    render() {
        const { tab, branch, data, loading, error } = this.state;
        const edit = canEdit();
        const events = data?.events ?? [], slots = data?.slots ?? [];
        const filter = this.state.filter !== ALL && events.some(e => e.eventId === this.state.filter) ? this.state.filter : ALL;
        const now = Date.now();
        const count = (st) => slots.filter(s => slotStatus(s, now) === st).length;
        return (
            <PageContainer maxWidth={1440}>
                <SectionHeader title="Events">
                    {data && <Tooltip title="The branch's events stamp: it grows on every write, and players re-read their events when it moves">
                        <Chip size="small" label={`stamp ${data.stamp}`} />
                    </Tooltip>}
                    {!edit && <Chip size="small" label="read only" />}
                    <IconButton size="small" onClick={() => this.load()} disabled={loading}><RefreshIcon fontSize="small" /></IconButton>
                    <Button variant="outlined" size="small" onClick={this.openPreview}>Preview for a player</Button>
                    {edit && tab === "definitions" && <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={this.openNewEvent}>New event</Button>}
                    {edit && tab === "schedule" && <Button variant="contained" size="small" startIcon={<AddIcon />} disabled={events.length === 0} onClick={() => this.openNewSlot()}>New slot</Button>}
                </SectionHeader>
                <Tabs value={tab} onChange={this.setTab} sx={{ mb: 2 }}>
                    {TABS.map(([k, l]) => <Tab key={k} value={k} label={l} />)}
                </Tabs>
                <SelectorBar sx={{ alignItems: "center" }}>
                    <SelectField label="Branch" size="small" minWidth={160} value={branch} options={globals.branches} onChange={this.changeBranch} />
                    {tab === "schedule" && <SelectField label="Event" size="small" minWidth={200} value={filter}
                                                        options={[{ value: ALL, label: "All events" }, ...events.map(e => e.eventId)]} onChange={f => this.setState({ filter: f })} />}
                    {data && (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: "auto", textAlign: "right" }}>
                            {plural(events.length, "event")} · {plural(slots.length, "slot")}: {count("live")} live, {count("upcoming")} upcoming
                            <br />Times are UTC. Players see a change at their next session sync.
                        </Typography>
                    )}
                </SelectorBar>
                {loading && <LinearProgress sx={{ mb: 1 }} />}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {tab === "definitions" ? <>{this.renderSheet()}{this.renderDefinitions()}</> : <>{this.renderSchedule(filter)}{this.renderProgress()}</>}
                {this.renderEventDialog()}
                {this.renderTablesDialog()}
                {this.renderSlotDialog()}
                {this.renderUploadDialog()}
                {this.renderConfirmDialog()}
                {this.renderPreviewDialog()}
            </PageContainer>
        );
    }
}
